/**
 * Add or edit one thing on a traveller's ground itinerary, from the manifest.
 *
 * Purpose-built rather than reusing `ItineraryItemDialog`: that dialog is bound
 * to `itineraryApi` + `trips/:id`, which is owner-scoped and therefore breaks for
 * an organizer the moment an attendee CLAIMS their trip (§ G7). This one posts
 * through the group, which authorises from the membership. Same field set, same
 * ui primitives, same type vocabulary — so it looks like the itinerary's dialog
 * without inheriting the wrong write path.
 *
 * ⚠️ `is_anchor` is not offered here at all. The anchor is a trip's single
 * feasibility deadline and every flight verdict is computed against it; the
 * group's meeting is this page's anchor. Two would mean two different meetings.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { travelGroupApi, type ManifestItem } from '@/lib/api';
import type { ItineraryItemInput, ItineraryItemType } from '@/lib/types';
import { ITEM_LABEL, ITEM_TYPES } from '@/components/trips/itinerary/itinerary-utils';

/** What the manifest opened the dialog for. */
export interface ManifestItemTarget {
  memberId: string;
  personName: string;
  dayDate: string;
  itemType: ItineraryItemType;
  /** Set when editing an existing row. */
  editing?: ManifestItem | null;
}

interface Props {
  groupId: string;
  target: ManifestItemTarget | null;
  onClose: () => void;
  /**
   * The meeting's PLACE, prefilled into "Address or place".
   *
   * ⚠️ Deliberately not into City. `travel_groups.venue` is the organizer's own
   * words for a venue ("Alpharetta office, 3 Ravinia Dr") — putting that in a
   * City box is wrong, and a walk on 2026-08-18 showed exactly that. The only
   * other candidate was `destination_airport`, and a bare IATA code in a City
   * field is § R7.
   */
  defaultPlace?: string | null;
}

/** A gentle, fully editable default start time per type. */
const TIME_HINT: Partial<Record<ItineraryItemType, string>> = {
  lodging: '15:00',
  dining: '19:00',
  activity: '10:00',
};

export default function ManifestItemDialog({ groupId, target, onClose, defaultPlace }: Props) {
  const queryClient = useQueryClient();
  const [itemType, setItemType] = useState<ItineraryItemType>('activity');
  const [title, setTitle] = useState('');
  const [dayDate, setDayDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [city, setCity] = useState('');
  const [location, setLocation] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [cost, setCost] = useState('');
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const open = target !== null;

  // Hydrate on open: an edit shows what is there, a create shows the type and
  // date the "+" was clicked next to.
  useEffect(() => {
    if (!target) return;
    setErr(null);
    const e = target.editing;
    if (e) {
      setItemType(e.item_type);
      setTitle(e.title);
      setDayDate(e.day_date ?? target.dayDate);
      setStartTime(e.start_time ?? '');
      setEndTime(e.end_time ?? '');
      setCity(e.city ?? '');
      setLocation(e.location ?? '');
      setConfirmation(e.confirmation ?? '');
      setCost(e.cost === null || e.cost === undefined ? '' : String(e.cost));
      setNotes(e.notes ?? '');
    } else {
      setItemType(target.itemType);
      setTitle('');
      setDayDate(target.dayDate);
      setStartTime(TIME_HINT[target.itemType] ?? '');
      setEndTime('');
      setCity('');
      setLocation(defaultPlace ?? '');
      setConfirmation('');
      setCost('');
      setNotes('');
    }
  }, [target, defaultPlace]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!target) throw new Error('Nothing to save');
      const parsedCost = cost.trim() === '' ? null : Number(cost);
      const body: ItineraryItemInput = {
        item_type: itemType,
        title: title.trim(),
        day_date: dayDate,
        start_time: startTime || null,
        end_time: endTime || null,
        city: city.trim() || null,
        location: location.trim() || null,
        notes: notes.trim() || null,
        confirmation: confirmation.trim() || null,
        // ⚠️ `Number('')` is 0 and 0 is a PRICE. An empty box means "unpriced",
        // never "free", so it must stay null all the way to the column.
        cost: parsedCost !== null && Number.isFinite(parsedCost) ? parsedCost : null,
      };
      return target.editing
        ? travelGroupApi.updateManifestItem(groupId, target.memberId, target.editing.id, body)
        : travelGroupApi.addManifestItem(groupId, target.memberId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-manifest', groupId] });
      onClose();
    },
    onError: (e: any) => {
      setErr(e?.response?.data?.error ?? e?.message ?? 'Could not save that');
    },
  });

  const invalid = title.trim() === '' || !/^\d{4}-\d{2}-\d{2}$/.test(dayDate);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {target?.editing ? 'Edit' : 'Add'} for {target?.personName ?? 'this traveller'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="mi-type">What is it?</Label>
              <Select value={itemType} onValueChange={(v) => setItemType(v as ItineraryItemType)}>
                <SelectTrigger id="mi-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ITEM_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {ITEM_LABEL[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="mi-date">Date</Label>
              <Input id="mi-date" type="date" value={dayDate} onChange={(e) => setDayDate(e.target.value)} />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="mi-title">
              {itemType === 'lodging' ? 'Hotel name' : 'Title'}
            </Label>
            <Input
              id="mi-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={itemType === 'lodging' ? 'Omni Atlanta at Centennial Park' : 'Team dinner'}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="mi-start">{itemType === 'lodging' ? 'Check-in' : 'Starts'}</Label>
              <Input id="mi-start" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="mi-end">{itemType === 'lodging' ? 'Check-out' : 'Ends'}</Label>
              <Input id="mi-end" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="mi-city">City</Label>
              <Input id="mi-city" value={city} onChange={(e) => setCity(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="mi-conf">Confirmation</Label>
              <Input
                id="mi-conf"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                placeholder="OM4482K"
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="mi-loc">Address or place</Label>
            <Input id="mi-loc" value={location} onChange={(e) => setLocation(e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="mi-cost">Cost (optional)</Label>
              <Input
                id="mi-cost"
                type="number"
                min="0"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder="leave blank if unknown"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="mi-notes">Notes</Label>
              <Input id="mi-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>

          {err && (
            <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1.5 text-xs text-destructive">
              {err}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={invalid || mutation.isPending}>
            {mutation.isPending ? 'Saving…' : target?.editing ? 'Save' : 'Add it'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
